import { bind } from '@myre/common-primitives';

import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  ComponentFactoryResolver,
  inject,
  Injectable,
  Injector,
  ViewContainerRef,
  type ComponentRef,
  type EmbeddedViewRef,
  type ViewRef,
} from '@angular/core';

const pipe =
  (...functions: ((data: any) => any)[]) =>
  (data: any) =>
    functions.reduce((value, func) => func(value), data);

@Injectable({ providedIn: 'root' })
export class DomService {
  private _document = inject(DOCUMENT);
  private _componentFactoryResolver = inject(ComponentFactoryResolver);
  private _appRef = inject(ApplicationRef);
  private _injector = inject(Injector);

  constructor() {
    this._factory = bind(this._factory, this);
    this._attach = bind(this._attach, this);
    this._dom = bind(this._dom, this);
  }

  public shiftIn({
    component,
    inTarget,
  }: {
    component: any;
    inTarget: ViewContainerRef;
  }): ComponentRef<any> {
    const { componentRef } = pipe(this._factory)({ component, inTarget });

    inTarget.insert(componentRef.hostView, 0);

    return componentRef;
  }

  public appendToBody(component: any): ComponentRef<any> {
    const { componentRef, domElem } = pipe(
      this._factory,
      this._attach,
      this._dom,
    )({ component });

    this._document.body.appendChild(domElem);

    return componentRef;
  }

  public shiftInBody(component: any): ComponentRef<any> {
    const { componentRef, domElem } = pipe(
      this._factory,
      this._attach,
      this._dom,
    )({ component });

    this._document.body.insertBefore(domElem, document.body.firstChild);

    return componentRef;
  }

  public destroy(componentRef: ComponentRef<any>): void {
    this._appRef.detachView(componentRef.hostView);
    componentRef.destroy();
  }

  /**
   * 1. Create a component reference from the component
   */
  private _factory({
    component,
    inTarget,
  }: {
    component: any;
    inTarget: ViewContainerRef;
  }): { componentRef: ComponentRef<any> } {
    const factory =
      this._componentFactoryResolver.resolveComponentFactory(component);
    const componentRef = factory.create(
      inTarget ? inTarget.injector : this._injector,
    );

    return { componentRef };
  }

  /**
   * 2. Attach component to the appRef so that it's inside the ng component tree
   */
  private _attach({ componentRef }: { componentRef: ComponentRef<any> }): {
    componentRef: ComponentRef<any>;
    viewRef: ViewRef;
  } {
    const viewRef = componentRef.hostView;
    this._appRef.attachView(componentRef.hostView);

    return { componentRef, viewRef };
  }

  /**
   * 3. Get DOM element from component
   */
  private _dom({
    componentRef,
    viewRef,
  }: {
    componentRef: ComponentRef<any>;
    viewRef: ViewRef;
  }): {
    componentRef: ComponentRef<any>;
    viewRef: ViewRef;
    domElem: HTMLElement;
  } {
    const domElem = (viewRef as EmbeddedViewRef<any>)
      .rootNodes[0] as HTMLElement;

    return { componentRef, viewRef, domElem };
  }
}
